import { api } from "@/lib/apiClient";

export type ProductLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucherType: string | null;
  particular: string;
  description: string;
  accountId: number;
  accountName: string;
  accountCode: string;
  partyId: number | null;
  partyName: string | null;
  debit: number;
  credit: number;
};

export type ProductLedger = {
  product: { id: number; name: string };
  accounts: {
    id: number;
    name: string;
    code: string;
    accountTypeCode: string;
    accountTypeName: string;
    detailTypeCode: string;
    detailTypeName: string;
  }[];
  lines: ProductLedgerLine[];
  totals: {
    inventoryCost: number;
    supplierPayable: number;
    partnerReceivable: number;
    partnerCapital: number;
    vehicleCost: number;
    journalDebit: number;
    journalCredit: number;
    debit: number;
    credit: number;
    difference: number;
  };
};

type ApiProductLedger = {
  product?: { id?: number; name?: string };
  accounts?: {
    id?: number;
    name?: string;
    code?: string;
    account_type_code?: string;
    account_type_name?: string;
    detail_type_code?: string;
    detail_type_name?: string;
  }[];
  lines?: {
    id?: number;
    date?: string | null;
    voucher?: string | null;
    voucher_type?: string | null;
    particular?: string;
    description?: string;
    account_id?: number;
    account_name?: string;
    account_code?: string;
    party_id?: number | null;
    party_name?: string | null;
    debit?: number | string;
    credit?: number | string;
  }[];
  totals?: {
    inventory_cost?: number | string;
    supplier_payable?: number | string;
    partner_receivable?: number | string;
    partner_capital?: number | string;
    vehicle_cost?: number | string;
    journal_debit?: number | string;
    journal_credit?: number | string;
    debit?: number | string;
    credit?: number | string;
    difference?: number | string;
  };
};

export type ProductMoneyFlowVendor = {
  name: string;
  totalAmount: number;
};

export type ProductMoneyFlowHeroCard = {
  title: string;
  partyLabel: string;
  partyName: string;
  partyPhone?: string | null;
  vendors?: ProductMoneyFlowVendor[];
  vendorCount?: number;
  partnerCount?: number;
  amount: number;
  settledLabel?: string;
  color: "green" | "red" | string;
  settledAmount: number;
  settledPct: number;
  pctLabel: string;
  outstandingAmount: number;
  excessAmount?: number;
  footerLeft: string;
  footerRight: string;
};

export type ProductMoneyFlowBreakdownItem = {
  title: string;
  subtitle?: string | null;
  amount: number;
  kind?: "cost" | "payment" | string;
};

export type ProductMoneyFlowTransaction = {
  id: string;
  date: string;
  refCode: string;
  title: string;
  category: string;
  description: string;
  party: string;
  partyType: string;
  flow: "receivable" | "payable";
  amount: number;
  requiredAmount: number;
  settledAmount: number;
  dueAmount: number;
  excessAmount: number;
  status: string;
  statusLabel: string;
  settlementText: string;
  breakdownKind?: "cost" | "payments" | "mixed" | string | null;
  breakdownItems: ProductMoneyFlowBreakdownItem[];
};

export type ProductMoneyFlowReport = {
  product: {
    id: number;
    name: string;
    refId: string;
    purchaseDate: string;
    saleDate: string | null;
    saleInvoiceNo: string | null;
    isSold: boolean;
    statusLabel: string;
  };
  hero: {
    card1: ProductMoneyFlowHeroCard;
    card2: ProductMoneyFlowHeroCard;
  };
  profitability: {
    saleAmount: number;
    saleInvoiceNo: string | null;
    vehicleCost: number;
    netProfit: number;
    showroomCommission: number;
    showroomPercent: number;
    partnerDistributionCount: number;
  } | null;
  transactions: ProductMoneyFlowTransaction[];
  summary: {
    totalCount: number;
    sumAmount: number;
  };
  currency: string;
  generatedAt?: string | null;
};

type ApiMoneyFlowVendor = {
  name?: string;
  total_amount?: number | string;
};

type ApiMoneyFlowHeroCard = {
  title?: string;
  party_label?: string;
  party_name?: string;
  party_phone?: string | null;
  vendors?: ApiMoneyFlowVendor[];
  vendor_count?: number | string;
  partner_count?: number | string;
  amount?: number | string;
  settled_label?: string;
  color?: string;
  settled_amount?: number | string;
  settled_pct?: number | string;
  pct_label?: string;
  outstanding_amount?: number | string;
  excess_amount?: number | string;
  footer_left?: string;
  footer_right?: string;
};

type ApiMoneyFlowBreakdownItem = {
  title?: string;
  subtitle?: string | null;
  amount?: number | string;
  kind?: string;
};

type ApiMoneyFlowTransaction = {
  id?: string | number;
  date?: string;
  ref_code?: string;
  title?: string;
  category?: string;
  description?: string;
  party?: string;
  party_type?: string;
  flow?: string;
  amount?: number | string;
  required_amount?: number | string;
  settled_amount?: number | string;
  due_amount?: number | string;
  excess_amount?: number | string;
  status?: string;
  status_label?: string;
  settlement_text?: string;
  breakdown_kind?: string | null;
  breakdown_items?: ApiMoneyFlowBreakdownItem[];
};

type ApiMoneyFlowReport = {
  product?: {
    id?: number;
    name?: string;
    ref_id?: string;
    purchase_date?: string;
    sale_date?: string | null;
    sale_invoice_no?: string | null;
    is_sold?: boolean;
    status_label?: string;
  };
  hero?: {
    card1?: ApiMoneyFlowHeroCard;
    card2?: ApiMoneyFlowHeroCard;
  };
  profitability?: {
    sale_amount?: number | string;
    sale_invoice_no?: string | null;
    vehicle_cost?: number | string;
    net_profit?: number | string;
    showroom_commission?: number | string;
    showroom_percent?: number | string;
    partner_distribution_count?: number | string;
  } | null;
  transactions?: ApiMoneyFlowTransaction[];
  summary?: {
    total_count?: number | string;
    sum_amount?: number | string;
  };
  currency?: string;
  generatedAt?: string | null;
  generated_at?: string | null;
};

function num(value: number | string | null | undefined, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function mapHeroCard(raw?: ApiMoneyFlowHeroCard): ProductMoneyFlowHeroCard {
  return {
    title: raw?.title ?? "",
    partyLabel: raw?.party_label ?? "",
    partyName: raw?.party_name ?? "",
    partyPhone: raw?.party_phone ?? null,
    vendors: (raw?.vendors ?? []).map((v) => ({
      name: v.name ?? "",
      totalAmount: num(v.total_amount),
    })),
    vendorCount: raw?.vendor_count != null ? num(raw.vendor_count) : undefined,
    partnerCount: raw?.partner_count != null ? num(raw.partner_count) : undefined,
    amount: num(raw?.amount),
    settledLabel: raw?.settled_label,
    color: raw?.color ?? "green",
    settledAmount: num(raw?.settled_amount),
    settledPct: num(raw?.settled_pct),
    pctLabel: raw?.pct_label ?? "",
    outstandingAmount: num(raw?.outstanding_amount),
    excessAmount: num(raw?.excess_amount),
    footerLeft: raw?.footer_left ?? "",
    footerRight: raw?.footer_right ?? "",
  };
}

function mapTransaction(raw: ApiMoneyFlowTransaction): ProductMoneyFlowTransaction {
  const flow = raw.flow === "payable" ? "payable" : "receivable";
  const amount = num(raw.amount);
  const requiredAmount = raw.required_amount != null ? num(raw.required_amount) : amount;
  const settledAmount = num(raw.settled_amount);
  const dueAmount =
    raw.due_amount != null ? num(raw.due_amount) : Math.max(0, requiredAmount - settledAmount);
  const excessAmount =
    raw.excess_amount != null ? num(raw.excess_amount) : Math.max(0, settledAmount - requiredAmount);
  return {
    id: String(raw.id ?? ""),
    date: raw.date ?? "",
    refCode: raw.ref_code ?? "",
    title: raw.title ?? "",
    category: raw.category ?? "",
    description: raw.description ?? "",
    party: raw.party ?? "",
    partyType: raw.party_type ?? "Party",
    flow,
    amount,
    requiredAmount,
    settledAmount,
    dueAmount,
    excessAmount,
    status: raw.status ?? "",
    statusLabel: raw.status_label ?? "",
    settlementText: raw.settlement_text ?? "",
    breakdownKind: raw.breakdown_kind ?? null,
    breakdownItems: (raw.breakdown_items ?? []).map((item) => ({
      title: item.title ?? "",
      subtitle: item.subtitle ?? null,
      amount: num(item.amount),
      kind: item.kind ?? (item.subtitle === "Payment entry" ? "payment" : "cost"),
    })),
  };
}

export async function fetchProductLedger(productId: string | number): Promise<ProductLedger> {
  const response = await api.get<{ data: ApiProductLedger }>(`/api/v1/catalog/products/${productId}/ledger`);
  const raw = response.data.data ?? {};
  return {
    product: {
      id: Number(raw.product?.id ?? productId),
      name: raw.product?.name ?? "",
    },
    accounts: (raw.accounts ?? []).map((a) => ({
      id: Number(a.id ?? 0),
      name: a.name ?? "",
      code: a.code ?? "",
      accountTypeCode: a.account_type_code ?? "",
      accountTypeName: a.account_type_name ?? "",
      detailTypeCode: a.detail_type_code ?? "",
      detailTypeName: a.detail_type_name ?? "",
    })),
    lines: (raw.lines ?? []).map((line) => ({
      id: Number(line.id ?? 0),
      date: line.date ?? null,
      voucher: line.voucher ?? null,
      voucherType: line.voucher_type ?? null,
      particular: line.particular ?? line.account_name ?? "",
      description: line.description ?? "",
      accountId: Number(line.account_id ?? 0),
      accountName: line.account_name ?? "",
      accountCode: line.account_code ?? "",
      partyId: line.party_id != null ? Number(line.party_id) : null,
      partyName: line.party_name ?? null,
      debit: Number(line.debit ?? 0),
      credit: Number(line.credit ?? 0),
    })),
    totals: {
      inventoryCost: Number(raw.totals?.inventory_cost ?? 0),
      supplierPayable: Number(raw.totals?.supplier_payable ?? 0),
      partnerReceivable: Number(raw.totals?.partner_receivable ?? 0),
      partnerCapital: Number(raw.totals?.partner_capital ?? 0),
      vehicleCost: Number(raw.totals?.vehicle_cost ?? raw.totals?.inventory_cost ?? 0),
      journalDebit: Number(raw.totals?.journal_debit ?? raw.totals?.debit ?? 0),
      journalCredit: Number(raw.totals?.journal_credit ?? raw.totals?.credit ?? 0),
      debit: Number(raw.totals?.debit ?? raw.totals?.journal_debit ?? 0),
      credit: Number(raw.totals?.credit ?? raw.totals?.journal_credit ?? 0),
      difference: Number(raw.totals?.difference ?? 0),
    },
  };
}

export async function fetchProductMoneyFlowReport(
  productId: string | number,
): Promise<ProductMoneyFlowReport> {
  const response = await api.get<{ data: ApiMoneyFlowReport }>(
    `/api/v1/catalog/products/${productId}/ledger/report`,
  );
  const raw = response.data.data ?? {};
  const profitability = raw.profitability
    ? {
        saleAmount: num(raw.profitability.sale_amount),
        saleInvoiceNo: raw.profitability.sale_invoice_no ?? null,
        vehicleCost: num(raw.profitability.vehicle_cost),
        netProfit: num(raw.profitability.net_profit),
        showroomCommission: num(raw.profitability.showroom_commission),
        showroomPercent: num(raw.profitability.showroom_percent),
        partnerDistributionCount: num(raw.profitability.partner_distribution_count),
      }
    : null;

  return {
    product: {
      id: Number(raw.product?.id ?? productId),
      name: raw.product?.name ?? "",
      refId: raw.product?.ref_id ?? "",
      purchaseDate: raw.product?.purchase_date ?? "",
      saleDate: raw.product?.sale_date ?? null,
      saleInvoiceNo: raw.product?.sale_invoice_no ?? null,
      isSold: Boolean(raw.product?.is_sold),
      statusLabel: raw.product?.status_label ?? (raw.product?.is_sold ? "Sold" : "In Stock"),
    },
    hero: {
      card1: mapHeroCard(raw.hero?.card1),
      card2: mapHeroCard(raw.hero?.card2),
    },
    profitability,
    transactions: (raw.transactions ?? []).map(mapTransaction),
    summary: {
      totalCount: num(raw.summary?.total_count),
      sumAmount: num(raw.summary?.sum_amount),
    },
    currency: (raw.currency ?? "Rs.").trim() || "Rs.",
    generatedAt: raw.generatedAt ?? raw.generated_at ?? null,
  };
}

export async function downloadProductLedgerPdf(productId: string | number): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const response = await api.get<Blob>(`/api/v1/catalog/products/${productId}/ledger/pdf`, {
    responseType: "blob",
  });

  const disposition = String(response.headers?.["content-disposition"] ?? "");
  const matched = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
  const filename = matched?.[1]
    ? decodeURIComponent(matched[1].replace(/"/g, ""))
    : `product-ledger-${productId}-${stamp}.pdf`;

  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
